import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from '../../firebase.config';
import { useTableSort, SortableHeader } from '../../utils/tableSort';
import { useTablePagination } from '../../utils/tablePagination';
import PaginationControls from '../../utils/PaginationControls';
import { formatCurrency, formatDate, CollapsibleQuickTotals, ReportHeaderWithExport } from './CommonComponents';
import GlobalExportButtons from '../GlobalExportButtons';
import { getCollectionForCompany } from '../../utils/dataAdapter';
import { calculateGSTAmount, calculateLineTotal } from '../../utils/numberFormat';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from '../shared/AppSelect';
const normalizePartyId = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim().toLowerCase();
};

const DetailedSalesReport = ({ db, userId, appId, companyId, dateRange, selectedParty, setSelectedParty, parties, loading, setLoading, companyDetails }) => {
  const [reportData, setReportData] = useState([]);
  const [itemNames, setItemNames] = useState({});
  const [itemMeasurements, setItemMeasurements] = useState({});
  const [partyNames, setPartyNames] = useState({});
  const [partyOutstanding, setPartyOutstanding] = useState({});
  const [partyGstTypes, setPartyGstTypes] = useState({});
  const [totals, setTotals] = useState({
    totalSales: 0,
    totalPurchases: 0,
    totalPaymentsReceived: 0,
    totalPaymentsPaid: 0,
    netBalance: 0
  });
  // Cache for data to avoid re-fetching
  const [dataCache, setDataCache] = useState({});
  const [cacheKey, setCacheKey] = useState('');

  // Cache cleanup - remove old cache entries (older than 5 minutes)
  useEffect(() => {
    const cleanupCache = () => {
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      
      setDataCache(prev => {
        const cleaned = {};
        Object.keys(prev).forEach(key => {
          if (now - prev[key].timestamp < fiveMinutes) {
            cleaned[key] = prev[key];
          }
        });
        return cleaned;
      });
    };

    const interval = setInterval(cleanupCache, 60000); // Cleanup every minute
    return () => clearInterval(interval);
  }, []);

  // Table sorting and pagination
  const { sortedData, sortConfig, handleSort } = useTableSort(reportData, { key: 'date', direction: 'asc' });
  const pagination = useTablePagination(sortedData, 25);

  // Fetch item names and measurement units with lazy loading
  const fetchItemNames = async (itemIds) => {
    if (!itemIds || itemIds.length === 0) return {};
    
    try {
      const itemData = {};
      const itemsCollection = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
      if (!itemsCollection) return {};
      
      // Process items in batches of 10 for better performance
      const batchSize = 10;
      for (let i = 0; i < itemIds.length; i += batchSize) {
        const batch = itemIds.slice(i, i + batchSize);
        
        // Process batch in parallel
        const batchPromises = batch.map(async (itemId) => {
          try {
            const itemRef = doc(db, itemsCollection.path, itemId);
            const itemSnap = await getDoc(itemRef);
            if (itemSnap.exists()) {
              const data = itemSnap.data();
              return {
                itemId,
                data: {
                  name: data.name || data.itemName || data.productName || itemId,
                  quantityMeasurement: data.quantityMeasurement || 'Nos.'
                }
              };
            } else {
              return {
                itemId,
                data: {
                  name: itemId,
                  quantityMeasurement: 'Nos.'
                }
              };
            }
          } catch (itemError) {
            console.error(`Error fetching item ${itemId}:`, itemError);
            return {
              itemId,
              data: {
                name: itemId,
                quantityMeasurement: 'Nos.'
              }
            };
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        batchResults.forEach(({ itemId, data }) => {
          itemData[itemId] = data;
        });
      }
      
      return itemData;
    } catch (error) {
      console.error('Error fetching item names:', error);
      return {};
    }
  };

  // Fetch party names and outstanding balances with lazy loading
  const fetchPartyNames = async (partyIds) => {
    if (!partyIds || partyIds.length === 0) return { names: {}, outstanding: {}, gstTypes: {} };
    
    try {
      const partyNames = {};
      const partyOutstanding = {};
      const partyGstTypes = {};
      const partiesCollection = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
      if (!partiesCollection) return { names: {}, outstanding: {}, gstTypes: {} };
      
      // Process parties in batches of 10 for better performance
      const batchSize = 10;
      for (let i = 0; i < partyIds.length; i += batchSize) {
        const batch = partyIds.slice(i, i + batchSize);
        
        // Process batch in parallel
        const batchPromises = batch.map(async (partyId) => {
          try {
            const partyRef = doc(db, partiesCollection.path, partyId);
            const partySnap = await getDoc(partyRef);
            if (partySnap.exists()) {
              const data = partySnap.data();
              
              // CRITICAL: Get opening balance directly from party document
              // The field name in Firestore is 'openingBalance'
              const rawOpeningBalance = data.openingBalance;
              const parsedOpeningBalance = rawOpeningBalance !== undefined && rawOpeningBalance !== null
                ? parseFloat(rawOpeningBalance) || 0
                : 0;
              
              // Debug: Log what we're reading from database
              console.log(`🔍 fetchPartyNames: Party ${partyId} from database`);
              console.log(`   data.openingBalance (raw):`, rawOpeningBalance);
              console.log(`   data.openingBalance (parsed):`, parsedOpeningBalance);
              console.log(`   Full party data:`, data);
              
              return {
                partyId,
                data: {
                  name: data.firmName || data.name || data.partyName || partyId,
                  outstanding: {
                    openingBalance: parsedOpeningBalance,
                    manualBalance: parseFloat(data.manualBalance) || 0,
                    useManualBalance: data.useManualBalance || false,
                    currentOutstanding: data.useManualBalance ? 
                      (parseFloat(data.manualBalance) || 0) : 
                      parsedOpeningBalance
                  },
                  // Ensure openingBalance is always available at top level
                  openingBalance: parsedOpeningBalance,
                  gstType: data.gstType || 'Unregistered'
                }
              };
            } else {
              return {
                partyId,
                data: {
                  name: partyId,
                  outstanding: { openingBalance: 0, manualBalance: 0, useManualBalance: false, currentOutstanding: 0 },
                  gstType: 'Unregistered'
                }
              };
            }
          } catch (partyError) {
            console.error(`Error fetching party ${partyId}:`, partyError);
            return {
              partyId,
              data: {
                name: partyId,
                outstanding: { openingBalance: 0, manualBalance: 0, useManualBalance: false, currentOutstanding: 0 },
                gstType: 'Unregistered'
              }
            };
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        batchResults.forEach(({ partyId, data }) => {
          partyNames[partyId] = data.name;
          
          // CRITICAL: Get opening balance value from party data
          // Priority: data.openingBalance (direct) > data.outstanding.openingBalance (nested)
          const openingBalanceValue = data.openingBalance !== undefined && data.openingBalance !== null
            ? parseFloat(data.openingBalance) || 0
            : (data.outstanding?.openingBalance !== undefined && data.outstanding.openingBalance !== null
              ? parseFloat(data.outstanding.openingBalance) || 0
              : 0);
          
          // Store the entire outstanding object AND add openingBalance at top level
          partyOutstanding[partyId] = {
            ...data.outstanding,
            openingBalance: openingBalanceValue  // CRITICAL: Store at top level for direct access
          };
          
          // Debug log to verify what we're storing
          console.log(`📊 DetailedSalesReport fetchPartyNames: Party ${partyId}`);
          console.log(`   Raw data.openingBalance: ${data.openingBalance}`);
          console.log(`   Raw data.outstanding.openingBalance: ${data.outstanding?.openingBalance}`);
          console.log(`   Final openingBalanceValue: ${openingBalanceValue}`);
          console.log(`   Stored in partyOutstanding[${partyId}]:`, partyOutstanding[partyId]);
          
          partyGstTypes[partyId] = data.gstType;
        });
      }
      
      return { names: partyNames, outstanding: partyOutstanding, gstTypes: partyGstTypes };
    } catch (error) {
      console.error('Error fetching party names:', error);
      return { names: {}, outstanding: {}, gstTypes: {} };
    }
  };

  // Get GST values directly from database (no calculation needed)
  const getRowGST = (row, billGstCalculationType = 'excluding', companyDetails = null, partyId = null, partyGstTypesMap = {}) => {
    // Check if company is Composition or Unregistered - no GST calculation
    if (companyDetails) {
      const gstStatus = (companyDetails.gstStatus || companyDetails.registrationStatus || '').toLowerCase();
      const gstinType = (companyDetails.gstinType || companyDetails.gstType || '').toLowerCase();
      
      // Unregistered companies - no GST
      if (gstStatus === 'unregistered' || gstStatus === 'not_registered' || gstStatus === 'not registered') {
        return {
          sgst: 0,
          cgst: 0,
          igst: 0,
          totalGST: 0
        };
      }
      
      // Composition scheme companies - no GST calculation
      if (gstinType === 'composition' || gstinType === 'composite' || gstinType.includes('comp')) {
        return {
          sgst: 0,
          cgst: 0,
          igst: 0,
          totalGST: 0
        };
      }
      
      // Only show GST for Regular GST type companies
      const companyGstinType = companyDetails.gstinType || companyDetails.gstType || 'Regular';
      if (companyGstinType !== 'Regular' && companyGstinType.toLowerCase() !== 'regular') {
        return {
          sgst: 0,
          cgst: 0,
          igst: 0,
          totalGST: 0
        };
      }
    }

    // Check party GST type - no GST for Unregistered/Composition parties
    if (partyId && partyGstTypesMap[partyId]) {
      const partyGstType = partyGstTypesMap[partyId];
      if (partyGstType !== 'Regular' && partyGstType.toLowerCase() !== 'regular') {
        return {
          sgst: 0,
          cgst: 0,
          igst: 0,
          totalGST: 0
        };
      }
    }

    // Use GST values directly from database (only for Regular registered companies and Regular parties)
    const sgst = parseFloat(row.sgst) || 0;
    const cgst = parseFloat(row.cgst) || 0;
    const igst = parseFloat(row.igst) || 0;
    const total = parseFloat(row.total) || 0;
    const amount = parseFloat(row.amount) || 0;
    
    // Total GST = total - amount
    const totalGST = total - amount;

    return {
      sgst: sgst,
      cgst: cgst,
      igst: igst,
      totalGST: totalGST
    };
  };

  // Format measurement using qtyDisplay from database
  const formatMeasurement = (row) => {
    // Use qtyDisplay from database (e.g., "3x2x5", "10x5", "100")
    if (row.qtyDisplay) {
      return row.qtyDisplay;
    }
    
    // Fallback to old logic if qtyDisplay not available
    const length = parseFloat(row.length) || 0;
    const width = parseFloat(row.width) || 0;
    const height = parseFloat(row.height) || 0;
    const nos = parseFloat(row.nos) || 0;
    
    if (length > 0 && width > 0 && height > 0) {
      return `${length}×${width}×${height}`;
    } else if (length > 0 && width > 0) {
      return `${length}×${width}`;
    } else if (nos > 0) {
      return `${nos} pc`;
    }
    return '-';
  };

  // Format quantity with unit
  const formatQuantity = (row, itemId) => {
    const qty = parseFloat(row.qty) || 0;
    const unit = row.unit || row.quantityMeasurement || itemMeasurements[itemId] || 'Nos.';
    return `${qty} ${unit}`;
  };

  // Main data fetching function — only loads when a party is selected
  useEffect(() => {
    const fetchData = async () => {
      if (!db || !userId || !companyId || !dateRange?.start || !dateRange?.end) return;

      if (!selectedParty) {
        setReportData([]);
        setItemNames({});
        setItemMeasurements({});
        setPartyNames({});
        setPartyOutstanding({});
        setTotals({
          totalSales: 0,
          totalPurchases: 0,
          totalPaymentsReceived: 0,
          totalPaymentsPaid: 0,
          netBalance: 0
        });
        setLoading && setLoading(false);
        return;
      }

      // Create cache key based on parameters with version for cache busting
      const cacheVersion = 'v2.2'; // Increment when data structure changes
      const currentCacheKey = `${cacheVersion}-${companyId}-${dateRange.start.toISOString()}-${dateRange.end.toISOString()}-${selectedParty}`;
      
      // Check if we have cached data for this exact query (with 2 minute TTL)
      if (dataCache[currentCacheKey]) {
        const cachedData = dataCache[currentCacheKey];
        const cacheAge = Date.now() - cachedData.timestamp;
        const cacheTTL = 2 * 60 * 1000; // 2 minutes
        
        if (cacheAge < cacheTTL) {
          setReportData(cachedData.reportData);
          setItemNames(cachedData.itemNames);
          setItemMeasurements(cachedData.itemMeasurements);
          setPartyNames(cachedData.partyNames);
          setPartyOutstanding(cachedData.partyOutstanding);
          setTotals(cachedData.totals);
          setLoading && setLoading(false);
          return;
        } else {
          // Cache expired, remove it
          setDataCache(prev => {
            const updated = { ...prev };
            delete updated[currentCacheKey];
            return updated;
          });
        }
      }

      setLoading && setLoading(true);
      try {
        const startStr = new Date(dateRange.start).toISOString().split('T')[0];
        const endStr = new Date(dateRange.end).toISOString().split('T')[0];

        // Build base queries scoped by date (party filtering will be done client-side)
        const salesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills');
        const purchaseCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills');
        const paymentsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');

        let salesQ = salesCollectionRef ? query(
          salesCollectionRef,
          where('invoiceDate', '>=', startStr),
          where('invoiceDate', '<=', endStr),
          orderBy('invoiceDate', 'asc')
        ) : null;

        let purchaseQ = purchaseCollectionRef ? query(
          purchaseCollectionRef,
          where('billDate', '>=', startStr),
          where('billDate', '<=', endStr),
          orderBy('billDate', 'asc')
        ) : null;

        let paymentsQ;
        if (paymentsCollectionRef) {
          try {
            paymentsQ = query(
              paymentsCollectionRef,
              where('paymentDate', '>=', startStr),
              where('paymentDate', '<=', endStr)
            );
          } catch (error) {
            paymentsQ = paymentsCollectionRef;
          }
        }

        const normalizedSelectedParty = normalizePartyId(selectedParty);
        const matchesSelectedPartyValue = (...values) =>
          values.some(value => value && normalizePartyId(value) === normalizedSelectedParty);

        // Execute queries with proper error handling for missing indexes
        let salesSnap, purchaseSnap, paymentsSnap;
        
        try {
          salesSnap = salesQ ? await getDocs(salesQ) : { docs: [] };
        } catch (error) {
          // Fallback: load entire collection and filter client-side
          if (salesCollectionRef) {
            try {
              salesSnap = await getDocs(salesCollectionRef);
            } catch (fallbackError) {
              salesSnap = { docs: [] };
            }
          } else {
            salesSnap = { docs: [] };
          }
        }
        
        try {
          purchaseSnap = purchaseQ ? await getDocs(purchaseQ) : { docs: [] };
        } catch (error) {
          // Fallback: load entire collection and filter client-side
          if (purchaseCollectionRef) {
            try {
              purchaseSnap = await getDocs(purchaseCollectionRef);
            } catch (fallbackError) {
              purchaseSnap = { docs: [] };
            }
          } else {
            purchaseSnap = { docs: [] };
          }
        }
        
        try {
          paymentsSnap = paymentsQ ? await getDocs(paymentsQ) : { docs: [] };
        } catch (error) {
          // Fallback: load entire collection and filter client-side
          if (paymentsCollectionRef) {
            try {
              paymentsSnap = await getDocs(paymentsCollectionRef);
            } catch (fallbackError) {
              paymentsSnap = { docs: [] };
            }
          } else {
            paymentsSnap = { docs: [] };
          }
        }

        // Process sales data with optimized processing
        let allTransactions = [];
        let allItemIds = new Set();
        let allPartyIds = new Set();
        // Process sales bills with batch processing for better performance
        const processSalesBills = (docs, partyGstTypesMap = {}) => {
          docs.forEach((doc) => {
            const data = doc.data();
            const resolvedPartyId = data.partyId || data.party || data.partyCode || data.partyName || data.customerName || '';
            if (!matchesSelectedPartyValue(resolvedPartyId, data.partyName, data.customerName, data.party)) {
              return;
            }
            const partyId = resolvedPartyId;
            allPartyIds.add(partyId);
            
            // Client-side date filtering (always apply as fallback if query didn't filter)
            const saleDate = data.invoiceDate || data.date;
            if (saleDate) {
              const date = new Date(saleDate);
              // Normalize dates for comparison (ignore time)
              const saleDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
              const startDateOnly = new Date(dateRange.start.getFullYear(), dateRange.start.getMonth(), dateRange.start.getDate());
              const endDateOnly = new Date(dateRange.end.getFullYear(), dateRange.end.getMonth(), dateRange.end.getDate());
              
              if (saleDateOnly < startDateOnly || saleDateOnly > endDateOnly) {
                return; // Skip this sale if it's outside date range
              }
            }
            
            if (data.rows && Array.isArray(data.rows)) {
              data.rows.forEach((row, index) => {
                if (row.item) {
                  allItemIds.add(row.item);
                }
                
                const billGstCalculationType = data.gstCalculationType || 'excluding';
                const gst = getRowGST(row, billGstCalculationType, companyDetails, partyId, partyGstTypesMap);
                
                // Use database values directly (no calculation needed)
                const taxableAmount = parseFloat(row.amount) || 0;
                // For unregistered parties/companies: totalAmount should equal taxableAmount (no GST)
                // For registered parties: use row.total (includes GST)
                const totalAmount = gst.totalGST === 0 ? taxableAmount : (parseFloat(row.total) || 0);
                
                allTransactions.push({
                  id: `${doc.id}_${index}`,
                  date: new Date(data.invoiceDate),
                  type: 'INV',
                  refNo: data.number || data.invoiceNumber || data.billNumber || data.customFields?.number || doc.id,
                  partyId: partyId,
                  itemId: row.item,
                  itemName: row.itemName || '',
                  measurement: formatMeasurement(row),
                  quantity: formatQuantity(row, row.item),
                  rate: parseFloat(row.rate) || 0,
                  discount: parseFloat(row.discount) || 0,
                  taxableAmount: taxableAmount,
                  sgst: gst.sgst,
                  cgst: gst.cgst,
                  igst: gst.igst,
                  totalGST: gst.totalGST,
                  totalAmount: totalAmount,
                  tags: Array.isArray(row?.tags) ? row.tags.join(', ') : '',
                  ledger: '+', // INV: (+) Sales = money in
                  description: `Sales - ${row.itemName || 'Item'}`,
                  originalData: { ...data, rowIndex: index }
                });
              });
            }
          });
        };

        // First, collect all party IDs by processing sales bills (without GST processing)
        const salesBatchSize = 50;
        for (let i = 0; i < salesSnap.docs.length; i += salesBatchSize) {
          const batch = salesSnap.docs.slice(i, i + salesBatchSize);
          // First pass: just collect party IDs and item IDs
          batch.forEach((doc) => {
            const data = doc.data();
            const resolvedPartyId = data.partyId || data.party || data.partyCode || data.partyName || data.customerName || '';
            if (matchesSelectedPartyValue(resolvedPartyId, data.partyName, data.customerName, data.party)) {
              allPartyIds.add(resolvedPartyId);
              if (data.rows && Array.isArray(data.rows)) {
                data.rows.forEach((row) => {
                  if (row.item) {
                    allItemIds.add(row.item);
                  }
                });
              }
            }
          });
        }

        // Collect party IDs from purchase bills (first pass)
        const purchaseBatchSize = 50;
        for (let i = 0; i < purchaseSnap.docs.length; i += purchaseBatchSize) {
          const batch = purchaseSnap.docs.slice(i, i + purchaseBatchSize);
          // First pass: just collect party IDs and item IDs
          batch.forEach((doc) => {
            const data = doc.data();
            const resolvedPartyId = data.partyId || data.party || data.partyCode || data.partyName || data.customerName || '';
            if (matchesSelectedPartyValue(resolvedPartyId, data.partyName, data.customerName, data.party)) {
              allPartyIds.add(resolvedPartyId);
              if (data.rows && Array.isArray(data.rows)) {
                data.rows.forEach((row) => {
                  if (row.item) {
                    allItemIds.add(row.item);
                  }
                });
              }
            }
          });
        }

        // Process payments with optimized filtering
        const processPayments = (docs) => {
          docs.forEach((doc) => {
            const data = doc.data();
            const resolvedPartyId = data.partyId || data.party || data.partyCode || data.partyName || data.customerName || '';
            if (!matchesSelectedPartyValue(resolvedPartyId, data.partyName, data.customerName, data.party)) {
              return;
            }
            const partyId = resolvedPartyId;
            allPartyIds.add(partyId);
            
            // Skip ADV (Advance Allocation) entries - these are re-entries when advance is allocated to invoice
            const receiptNumber = (data.receiptNumber || data.referenceNumber || data.paymentNumber || data.number || '').toString().toUpperCase();
            if (receiptNumber.startsWith('ADV-INV') || receiptNumber.startsWith('ADV')) {
              // Skip advance allocation entries (ADV-INV*) as they are re-entries
              // Also skip pure advance payments (ADV*) that are not allocated
              // Only include actual payments (PRI/PRP) and khata payments
              return;
            }
            
            // Check if this is a khata payment (advance payment without bill reference)
            const isKhataPayment = data.paymentType === 'khata' || 
                                  data.paymentType === 'advance' ||
                                  (!data.billReference && !data.billId && !data.billNumber);
            
            // Skip pure advance payments that haven't been allocated (they're not transactions yet)
            // But include khata payments as they affect the ledger
            if (isKhataPayment && !data.billReference && !data.billId) {
              // This is a khata/advance payment - include it in the report
              // It will show as a payment received/paid entry
            }
            
            // Client-side date filtering for payments (always apply as fallback)
            const paymentDate = data.paymentDate || data.date;
            if (paymentDate) {
              const paymentDateObj = new Date(paymentDate);
              // Normalize dates for comparison (ignore time)
              const paymentDateOnly = new Date(paymentDateObj.getFullYear(), paymentDateObj.getMonth(), paymentDateObj.getDate());
              const startDateOnly = new Date(dateRange.start.getFullYear(), dateRange.start.getMonth(), dateRange.start.getDate());
              const endDateOnly = new Date(dateRange.end.getFullYear(), dateRange.end.getMonth(), dateRange.end.getDate());
              
              if (paymentDateOnly < startDateOnly || paymentDateOnly > endDateOnly) {
                return; // Skip this payment if it's outside date range
              }
            }
            
            // Try multiple possible amount fields
            const amount = parseFloat(data.originalPaymentAmount) || 
                         parseFloat(data.amount) || 
                         parseFloat(data.totalAmount) ||
                         parseFloat(data.paymentAmount) ||
                         0;
            
            if (amount <= 0) {
              return; // Skip zero or negative payments
            }
            
            // Check multiple possible fields for payment type
            // PRI = Payment Received (against sales/invoice)
            // PRP = Payment Paid (against purchase)
            // PRIORITIZE receiptNumber check over data.type/data.paymentType fields
            // Check for PRP first (more specific), then PRI
            // Handle cases where receiptNumber might be "PRI PRP25-26/18" - prioritize PRP
            // Check if receiptNumber contains PRP (either at start, or after space/other text)
            const hasPRP = receiptNumber && receiptNumber.length > 0 && (
              receiptNumber.startsWith('PRP') || 
              receiptNumber.includes(' PRP') ||
              /PRP\d/.test(receiptNumber) || // PRP followed by digit (e.g., PRP25-26/18)
              /\bPRP\b/i.test(receiptNumber) // PRP as word boundary
            );
            const hasPRI = !hasPRP && receiptNumber && receiptNumber.length > 0 && (
              receiptNumber.startsWith('PRI') || 
              /\bPRI\b/i.test(receiptNumber)
            );
            
            // Determine transaction type: PRIORITIZE receiptNumber (hasPRP/hasPRI) over other fields
            // If receiptNumber indicates PRP, it's PRP regardless of data.type
            // If receiptNumber indicates PRI, it's PRI regardless of data.type
            // Otherwise, fallback to data.type/data.paymentType
            let transactionType;
            if (hasPRP) {
              transactionType = 'PRP';
            } else if (hasPRI) {
              transactionType = 'PRI';
            } else {
              // Fallback to data.type/data.paymentType if receiptNumber doesn't indicate type
              const isReceived = data.type === 'received' || 
                               data.paymentType === 'received' || 
                               data.type === 'Payment Received' ||
                               data.paymentType === 'Payment Received' ||
                               (isKhataPayment && !data.billReference);
              const isForPurchase = data.type === 'paid' || 
                                  data.paymentType === 'paid' ||
                                  data.type === 'Payment Paid' ||
                                  data.paymentType === 'Payment Paid' ||
                                  (isKhataPayment && data.paymentType === 'paid');
              transactionType = isReceived ? 'PRI' : (isForPurchase ? 'PRP' : 'PRP'); // Default to PRP if unclear
            }
            const isPaymentReceived = transactionType === 'PRI';
            
            allTransactions.push({
              id: doc.id,
              date: new Date(data.paymentDate || data.date),
              type: transactionType,
              refNo: receiptNumber || doc.id,
              partyId: partyId,
              itemId: '',
              itemName: '',
              measurement: '-',
              quantity: '-',
              rate: 0,
              discount: 0,
              taxableAmount: 0,
              sgst: 0,
              cgst: 0,
              igst: 0,
              totalGST: 0,
              totalAmount: amount,
              ledger: isPaymentReceived ? '-' : '+', // PRI: (-) Payment received = money out (from customer), PRP: (+) Payment paid = money in (to supplier)
              description: isPaymentReceived ? 
                (isKhataPayment ? 'Khata Payment Received' : 'Payment Received') : 
                (isKhataPayment ? 'Khata Payment Paid' : 'Payment Paid'),
              originalData: data,
              isKhataPayment: isKhataPayment
            });
          });
        };

        // Process payments in batches for better performance
        const paymentBatchSize = 100;
        for (let i = 0; i < paymentsSnap.docs.length; i += paymentBatchSize) {
          const batch = paymentsSnap.docs.slice(i, i + paymentBatchSize);
          processPayments(batch);
        }

        // Sort transactions by date (since we removed orderBy from queries to avoid index issues)
        allTransactions.sort((a, b) => a.date - b.date);

        // Fetch item and party names
        const [itemDataMap, partyData] = await Promise.all([
          fetchItemNames(Array.from(allItemIds)),
          fetchPartyNames(Array.from(allPartyIds))
        ]);

        // Extract item names and measurements
        const itemNamesMap = {};
        const itemMeasurementsMap = {};
        Object.keys(itemDataMap).forEach(itemId => {
          itemNamesMap[itemId] = itemDataMap[itemId].name;
          itemMeasurementsMap[itemId] = itemDataMap[itemId].quantityMeasurement;
        });

        setItemNames(itemNamesMap);
        setItemMeasurements(itemMeasurementsMap);
        setPartyNames(partyData.names);
        setPartyOutstanding(partyData.outstanding);
        setPartyGstTypes(partyData.gstTypes || {});
        
        // CRITICAL: Store partyData.outstanding in a local variable to use directly
        // This avoids React state timing issues - state updates are async, so we use the data directly
        const partyOutstandingMap = partyData.outstanding;

        // Now process transactions with party GST types
        const partyGstTypesMap = partyData.gstTypes || {};
        
        // Process sales bills with party GST types
        for (let i = 0; i < salesSnap.docs.length; i += salesBatchSize) {
          const batch = salesSnap.docs.slice(i, i + salesBatchSize);
          processSalesBills(batch, partyGstTypesMap);
        }
        
        // Process purchase bills with party GST types  
        const processPurchaseBillsWithGST = (docs, partyGstTypesMap = {}) => {
          docs.forEach((doc) => {
            const data = doc.data();
            const resolvedPartyId = data.partyId || data.party || data.partyCode || data.partyName || data.customerName || '';
            if (!matchesSelectedPartyValue(resolvedPartyId, data.partyName, data.customerName, data.party)) {
              return;
            }
            const partyId = resolvedPartyId;
            
            // Client-side date filtering (always apply as fallback if query didn't filter)
            const purchaseDate = data.billDate || data.date;
            if (purchaseDate) {
              const date = new Date(purchaseDate);
              // Normalize dates for comparison (ignore time)
              const purchaseDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
              const startDateOnly = new Date(dateRange.start.getFullYear(), dateRange.start.getMonth(), dateRange.start.getDate());
              const endDateOnly = new Date(dateRange.end.getFullYear(), dateRange.end.getMonth(), dateRange.end.getDate());
              
              if (purchaseDateOnly < startDateOnly || purchaseDateOnly > endDateOnly) {
                return; // Skip this purchase if it's outside date range
              }
            }
            
            if (data.rows && Array.isArray(data.rows)) {
              data.rows.forEach((row, index) => {
                const billGstCalculationType = data.gstCalculationType || 'excluding';
                const gst = getRowGST(row, billGstCalculationType, companyDetails, partyId, partyGstTypesMap);
                
                // Use database values directly (no calculation needed)
                const taxableAmount = parseFloat(row.amount) || 0;
                // For unregistered parties/companies: totalAmount should equal taxableAmount (no GST)
                // For registered parties: use row.total (includes GST)
                const totalAmount = gst.totalGST === 0 ? taxableAmount : (parseFloat(row.total) || 0);
                
                allTransactions.push({
                  id: `${doc.id}_${index}`,
                  date: new Date(data.billDate),
                  type: 'PRB',
                  refNo: data.number || data.billNumber || data.invoiceNumber || data.customFields?.number || doc.id,
                  partyId: partyId,
                  itemId: row.item,
                  itemName: row.itemName || '',
                  measurement: formatMeasurement(row),
                  quantity: formatQuantity(row, row.item),
                  rate: parseFloat(row.rate) || 0,
                  discount: parseFloat(row.discount) || 0,
                  taxableAmount: taxableAmount,
                  sgst: gst.sgst,
                  cgst: gst.cgst,
                  igst: gst.igst,
                  totalGST: gst.totalGST,
                  totalAmount: totalAmount,
                  ledger: '-', // PRB: (-) Purchase = money out
                  description: `Purchase - ${row.itemName || 'Item'}`,
                  originalData: { ...data, rowIndex: index }
                });
              });
            }
          });
        };
        
        for (let i = 0; i < purchaseSnap.docs.length; i += purchaseBatchSize) {
          const batch = purchaseSnap.docs.slice(i, i + purchaseBatchSize);
          processPurchaseBillsWithGST(batch, partyGstTypesMap);
        }

        // Group by party for detailed view
        const groupedData = {};
        allTransactions.forEach(transaction => {
          const partyId = transaction.partyId;
          if (!groupedData[partyId]) {
            groupedData[partyId] = {
              partyId: partyId,
              partyName: partyData.names[partyId] || partyId,
              transactions: [],
              totals: {
                totalSales: 0,
                totalPurchases: 0,
                totalPaymentsReceived: 0,
                totalPaymentsPaid: 0,
                netBalance: 0
              }
            };
          }
          
          groupedData[partyId].transactions.push(transaction);
          
          // Update totals
          if (transaction.type === 'INV') {
            groupedData[partyId].totals.totalSales += transaction.totalAmount;
          } else if (transaction.type === 'PRB') {
            groupedData[partyId].totals.totalPurchases += transaction.totalAmount;
          } else if (transaction.type === 'PRI') {
            groupedData[partyId].totals.totalPaymentsReceived += transaction.totalAmount;
          } else if (transaction.type === 'PRP') {
            groupedData[partyId].totals.totalPaymentsPaid += transaction.totalAmount;
          }
        });

        // Calculate cumulative balance for each party's transactions
        Object.values(groupedData).forEach(party => {
          // CRITICAL: Sort transactions by date FIRST (chronologically) before calculating balance
          // Balance must be calculated in chronological order for accurate cumulative balance
          party.transactions.sort((a, b) => {
            const dateA = a.date instanceof Date ? a.date : new Date(a.date);
            const dateB = b.date instanceof Date ? b.date : new Date(b.date);
            return dateA - dateB; // Ascending: oldest first
          });
          
          // Start with opening balance from parties data
          // CRITICAL: Use partyOutstandingMap directly (from partyData.outstanding) instead of React state
          // React state updates are async, so using state here would give us empty object {}
          // Using partyOutstandingMap ensures we have the data immediately
          const partyOutstandingData = partyOutstandingMap[party.partyId];
          let openingBalance = 0;
          
          if (partyOutstandingData) {
            // First try direct openingBalance field (stored at top level)
            if (partyOutstandingData.openingBalance !== undefined && partyOutstandingData.openingBalance !== null) {
              openingBalance = parseFloat(partyOutstandingData.openingBalance) || 0;
            } 
            // Then try outstanding.openingBalance (nested in outstanding object)
            else if (partyOutstandingData.outstanding?.openingBalance !== undefined && partyOutstandingData.outstanding.openingBalance !== null) {
              openingBalance = parseFloat(partyOutstandingData.outstanding.openingBalance) || 0;
            }
            // Finally fallback to currentOutstanding
            else if (partyOutstandingData.currentOutstanding !== undefined && partyOutstandingData.currentOutstanding !== null) {
              openingBalance = parseFloat(partyOutstandingData.currentOutstanding) || 0;
            }
          }
          
          // Debug log
          if (openingBalance !== 0) {
            console.log(`✅ DetailedSalesReport: Party ${party.partyId} (${party.partyName}) opening balance = ${openingBalance}`);
          }
          let runningBalance = openingBalance;
          
          // Calculate cumulative balance in chronological order
          party.transactions.forEach(transaction => {
            // Calculate amount based on transaction type
            // Balance represents: Outstanding/Receivable from company's perspective
            let amount = 0;
            if (transaction.type === 'INV') {
              // Sales Invoice: Customer owes company → Increases outstanding
              amount = transaction.totalAmount;
            } else if (transaction.type === 'PRB') {
              // Purchase Bill: Company owes party (if party is supplier) → Decreases net receivable
              amount = -transaction.totalAmount;
            } else if (transaction.type === 'PRI') {
              // Payment Received: Customer pays company → Decreases outstanding
              amount = -transaction.totalAmount;
            } else if (transaction.type === 'PRP') {
              // Payment Paid: Company pays party → Increases net receivable (decreases payable)
              amount = transaction.totalAmount;
            }
            
            runningBalance += amount;
            transaction.cumulativeBalance = runningBalance;
          });
          
          // Calculate net balance for each party
          party.totals.netBalance = runningBalance;
          party.totals.openingBalance = openingBalance;
        });

        const finalData = Object.values(groupedData).filter(
          party => normalizePartyId(party.partyId) === normalizedSelectedParty
        );

        setReportData(finalData);

        // Calculate overall totals
        const overallTotals = finalData.reduce((acc, party) => ({
          totalSales: acc.totalSales + party.totals.totalSales,
          totalPurchases: acc.totalPurchases + party.totals.totalPurchases,
          totalPaymentsReceived: acc.totalPaymentsReceived + party.totals.totalPaymentsReceived,
          totalPaymentsPaid: acc.totalPaymentsPaid + party.totals.totalPaymentsPaid,
          netBalance: acc.netBalance + party.totals.netBalance
        }), { totalSales: 0, totalPurchases: 0, totalPaymentsReceived: 0, totalPaymentsPaid: 0, netBalance: 0 });

        setTotals(overallTotals);

        // Cache the processed data
        const cacheData = {
          reportData: finalData,
          itemNames: itemNamesMap,
          itemMeasurements: itemMeasurementsMap,
          partyNames: partyData.names,
          partyOutstanding: partyData.outstanding,
          totals: overallTotals,
          timestamp: Date.now()
        };
        
        setDataCache(prev => ({
          ...prev,
          [currentCacheKey]: cacheData
        }));

      } catch (error) {
        console.error('Error fetching detailed sales report data:', error);
      } finally {
        setLoading && setLoading(false);
      }
    };

    fetchData();
  }, [db, userId, companyId, dateRange, selectedParty, companyDetails, parties]);

  // Prepare export data
  const getExportData = () => {
    const exportData = [];
    
    reportData.forEach(party => {
      // Add party header
      exportData.push({
        'Party': party.partyName,
        'Date': '',
        'Invoice/Ref': '',
        'Item': '',
        'Measurement': '',
        'Qty/Unit': '',
        'Rate': '',
        'Discount': '',
        'Tags': '',
        'Taxable Amount': '',
        'Total Tax': '',
        'Total Amount': '',
        'Ledger': ''
      });
      
      // Add transactions
      party.transactions.forEach(transaction => {
        exportData.push({
          'Party': '',
          'Date': formatDate(transaction.date),
          'Invoice/Ref': transaction.refNo,
          'Item': itemNames[transaction.itemId] || transaction.itemName || '-',
          'Measurement': transaction.measurement,
          'Qty/Unit': transaction.quantity,
          'Rate': transaction.rate,
          'Discount': transaction.discount,
          'Tags': transaction.tags || '',
          'Taxable Amount': transaction.taxableAmount,
          'Total Tax': transaction.totalGST,
          'Total Amount': transaction.totalAmount,
          'Balance': transaction.cumulativeBalance
        });
      });
      
      // Add party totals
      exportData.push({
        'Party': `Total for ${party.partyName}`,
        'Date': '',
        'Invoice/Ref': '',
        'Item': '',
        'Measurement': '',
        'Qty/Unit': '',
        'Rate': '',
        'Discount': '',
        'Tags': '',
        'Taxable Amount': '',
        'Total Tax': '',
        'Total Amount': party.totals.netBalance,
        'Ledger': ''
      });
      
      // Add empty row
      exportData.push({
        'Party': '',
        'Date': '',
        'Invoice/Ref': '',
        'Item': '',
        'Measurement': '',
        'Qty/Unit': '',
        'Rate': '',
        'Discount': '',
        'Tags': '',
        'Taxable Amount': '',
        'Total Tax': '',
        'Total Amount': '',
        'Balance': '',
        'Ledger': ''
      });
    });
    
    return exportData;
  };

  const getExportColumns = () => [
    { key: 'Party', label: 'Party' },
    { key: 'Date', label: 'Date' },
    { key: 'Invoice/Ref', label: 'Invoice/Ref No' },
    { key: 'Item', label: 'Item' },
    { key: 'Measurement', label: 'Measurement' },
    { key: 'Qty/Unit', label: 'Qty/Unit' },
    { key: 'Rate', label: 'Rate' },
    { key: 'Discount', label: 'Discount' },
    { key: 'Tags', label: 'Tags' },
    { key: 'Taxable Amount', label: 'Taxable Amount' },
    { key: 'Total Tax', label: 'Total Tax' },
    { key: 'Total Amount', label: 'Total Amount' },
    { key: 'Balance', label: 'Balance' }
  ];

  const getReportDetails = () => ({
    'Period': `${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}`,
    'Total Sales': formatCurrency(totals.totalSales),
    'Total Purchases': formatCurrency(totals.totalPurchases),
    'Payments Received': formatCurrency(totals.totalPaymentsReceived),
    'Payments Paid': formatCurrency(totals.totalPaymentsPaid),
    'Net Balance': formatCurrency(totals.netBalance),
    dateRange
  });

  const selectedPartyName = parties.find(p => p.id === selectedParty)?.firmName
    || parties.find(p => p.id === selectedParty)?.name
    || parties.find(p => p.id === selectedParty)?.partyName
    || 'Selected Party';

  if (!selectedParty) {
    return (
      <div className="p-3 sm:p-6">
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-amber-900 mb-2">Select a Party First</h3>
              <p className="text-sm text-amber-800">
                Please select a party from the filter above to load its detailed sales report.
                This report shows transaction-level details for one party at a time.
              </p>
            </div>
            <div className="w-full sm:w-72 shrink-0">
              <AppSelect
                value={selectedParty}
                onChange={(e) => setSelectedParty && setSelectedParty(e.target.value)}
                aria-label="Select Party"
                sheetTitle="Select Party"
                placeholder="Choose a party..."
                options={parties.map((party) => ({
                  value: party.id,
                  label: party.firmName || party.name || party.partyName || party.id,
                }))}
                triggerClassName={`${APP_SELECT_FILTER_TRIGGER} focus:border-blue-500`}
              />
            </div>
          </div>
        </div>

        <ReportHeaderWithExport
          title="Detailed Sales Report"
          subtitle={`${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}`}
          exportButtons={null}
        />

        <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-gray-600 font-medium">No party selected</p>
          <p className="text-sm text-gray-500 mt-1">Select a party to view its sales, purchases, and payment details.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-3 sm:p-6">
        {/* Loading Header */}
        <div className="mb-6">
          <div className="h-8 bg-gray-200 rounded w-64 mb-2 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-96 animate-pulse"></div>
        </div>
        
        {/* Loading Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="h-4 bg-gray-200 rounded w-20 mb-2 animate-pulse"></div>
              <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
            </div>
          ))}
        </div>
        
        {/* Loading Table Skeleton */}
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Party Header Skeleton */}
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
                  <div className="flex space-x-4">
                    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                  </div>
                </div>
              </div>
              
              {/* Table Skeleton */}
              <div className="p-4">
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((j) => (
                    <div key={j} className="grid grid-cols-10 gap-4">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Loading Indicator */}
        <div className="flex justify-center items-center mt-8">
          <div className="flex items-center space-x-2 text-blue-600">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-sm font-medium">Loading detailed sales report...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6">
      {/* Party Filter Section */}
      <div className="mb-6 bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Selected Party</h3>
            <p className="text-sm text-gray-600">
              Showing details for: <span className="font-medium text-gray-800">{selectedPartyName}</span>
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <AppSelect
              value={selectedParty}
              onChange={(e) => setSelectedParty && setSelectedParty(e.target.value)}
              aria-label="Select Party"
              sheetTitle="Select Party"
              options={parties.map((party) => ({
                value: party.id,
                label: party.firmName || party.name || party.partyName || party.id,
              }))}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} focus:border-blue-500`}
            />
            <button
              onClick={() => setSelectedParty && setSelectedParty('')}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Change Party
            </button>
          </div>
        </div>
      </div>

      {/* Report Header with Export Buttons */}
      <ReportHeaderWithExport
        title="Detailed Sales Report"
        subtitle={`${formatDate(dateRange.start)} to ${formatDate(dateRange.end)} • ${selectedPartyName}`}
        exportButtons={
          <GlobalExportButtons
            data={getExportData()}
            columns={getExportColumns()}
            filename="detailed-sales-report"
            reportDetails={getReportDetails()}
            title="Detailed Sales Report"
          />
        }
      >
        <CollapsibleQuickTotals
          cards={[
            { label: 'Total Sales', value: formatCurrency(totals.totalSales), color: 'blue' },
            { label: 'Total Purchases', value: formatCurrency(totals.totalPurchases), color: 'green' },
            { label: 'Payments Received', value: formatCurrency(totals.totalPaymentsReceived), color: 'yellow' },
            { label: 'Payments Paid', value: formatCurrency(totals.totalPaymentsPaid), color: 'red' },
            { label: 'Net Balance', value: formatCurrency(totals.netBalance), color: 'purple' }
          ]}
        />
      </ReportHeaderWithExport>

      {/* Report Content */}
      {reportData.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">No data found for the selected period.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {reportData.map((party, partyIndex) => (
            <div key={party.partyId} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Party Header */}
              <div className="bg-gray-50 px-3 sm:px-4 py-3 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800">{party.partyName}</h3>
                  <div className="text-sm text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
                    <span>
                      Opening: <span className="font-semibold text-blue-600">
                        {formatCurrency(party.totals.openingBalance || 0)}
                      </span>
                    </span>
                    <span>
                      Net Balance: <span className={`font-semibold ${party.totals.netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(party.totals.netBalance)}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Mobile transaction cards */}
              <div className="md:hidden p-3 space-y-2">
                {party.transactions.map((transaction) => (
                  <div key={transaction.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                    <div className="flex justify-between items-start gap-2 mb-1">
                      <span className="text-xs text-gray-500">{formatDate(transaction.date)}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        transaction.type === 'INV' ? 'bg-blue-100 text-blue-800' :
                        transaction.type === 'PRB' ? 'bg-green-100 text-green-800' :
                        transaction.type === 'PRI' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>{transaction.type}</span>
                    </div>
                    <div className="text-sm font-medium text-gray-900 mb-1">{transaction.refNo}</div>
                    <div className="text-sm text-gray-700 mb-2">{itemNames[transaction.itemId] || transaction.itemName || '—'}</div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600">
                      <span>Qty: {transaction.quantity}</span>
                      <span>Rate: {formatCurrency(transaction.rate)}</span>
                      <span>Taxable: {formatCurrency(transaction.taxableAmount)}</span>
                      <span>GST: {formatCurrency(transaction.totalGST)}</span>
                    </div>
                    <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100 text-sm">
                      <span className="font-semibold">{formatCurrency(transaction.totalAmount)}</span>
                      <span className={`font-medium ${transaction.cumulativeBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        Bal: {formatCurrency(transaction.cumulativeBalance)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Transactions Table with Pagination */}
              <div className="hidden md:block report-scroll-x">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-20">
                        Invoice/Ref
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Measurement
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qty/Unit
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rate
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Discount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tags
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Taxable Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Tax
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Balance
                      </th>
                    </tr>
                  </thead>
                   <tbody className="bg-white divide-y divide-gray-200">
                     {party.transactions.map((transaction, index) => (
                      <tr key={transaction.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatDate(transaction.date)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 sticky left-0 bg-white z-10">
                          <div className="flex items-center">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              transaction.type === 'INV' ? 'bg-blue-100 text-blue-800' :
                              transaction.type === 'PRB' ? 'bg-green-100 text-green-800' :
                              transaction.type === 'PRI' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {transaction.type}
                            </span>
                            <span className="ml-2">{transaction.refNo}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {itemNames[transaction.itemId] || transaction.itemName || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {transaction.measurement}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {transaction.quantity}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatCurrency(transaction.rate)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {transaction.discount}%
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {transaction.tags || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatCurrency(transaction.taxableAmount)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatCurrency(transaction.totalGST)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          {formatCurrency(transaction.totalAmount)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          <span className={`${transaction.cumulativeBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(transaction.cumulativeBalance)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                 
                 {/* Transaction count info */}
                 <div className="bg-gray-50 px-4 py-3 text-center">
                   <p className="text-sm text-gray-600">
                     Showing all {party.transactions.length} transactions for {party.partyName}.
                   </p>
                 </div>
              </div>

              {/* Party Totals */}
              <div className="bg-gray-100 px-4 py-3 border-t border-gray-200">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Opening:</span>
                    <span className="ml-2 font-semibold text-blue-600">{formatCurrency(party.totals.openingBalance || 0)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Sales:</span>
                    <span className="ml-2 font-semibold text-blue-600">{formatCurrency(party.totals.totalSales)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Purchases:</span>
                    <span className="ml-2 font-semibold text-green-600">{formatCurrency(party.totals.totalPurchases)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Payments Received:</span>
                    <span className="ml-2 font-semibold text-yellow-600">{formatCurrency(party.totals.totalPaymentsReceived)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Payments Paid:</span>
                    <span className="ml-2 font-semibold text-red-600">{formatCurrency(party.totals.totalPaymentsPaid)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Net Balance:</span>
                    <span className={`ml-2 font-semibold ${party.totals.netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(party.totals.netBalance)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DetailedSalesReport;
